import { useState, useRef, useEffect } from 'react';
import { db } from '../../../firebase';
import { ref, get, remove } from 'firebase/database';
import { apiFetch } from '../../../utils/api';
import { useBotWorkspace } from '../../../hooks/useBotWorkspace';
import { useDashboardChrome } from '../../../hooks/useDashboardChrome';
import { formatPhone, API_BASE } from '../../../utils/botHelpers';

// WhatsApp and Facebook each have their own dedicated agent-handoff routes
// (wa-agent / fb-agent) since their sender-key prefixes and send APIs
// differ; everything else (website, voice, ai) shares the generic ones.
const agentEndpoint = (botType?: string) =>
  botType === 'whatsapp' ? 'wa-agent' : botType === 'facebook' ? 'fb-agent' : 'agent';
const agentChannelLabel = (botType?: string) =>
  botType === 'whatsapp' ? 'WhatsApp' : botType === 'facebook' ? 'Facebook' : 'website';

export default function ConversationsRoute() {
  const { bot, analytics, contacts, refreshAnalytics, releasingRef, releasedSenders } = useBotWorkspace();
  const { currentUser } = useDashboardChrome();

  const [searchPhone, setSearchPhone] = useState('');
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [agentModes, setAgentModes] = useState<Record<string, boolean>>({});
  const [agentReply, setAgentReply] = useState('');
  const [agentSending, setAgentSending] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);

  const hasActiveAgentRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const agentInputRef = useRef<HTMLInputElement>(null);

  const loadAgentModes = async (botId: string) => {
    try {
      const snap = await get(ref(db, `agentMode/${botId}`));
      const data: Record<string, boolean> = {};
      const now = Date.now();
      const twoHours = 2 * 60 * 60 * 1000;
      if (snap.exists()) {
        const staleKeys: string[] = [];
        snap.forEach(c => {
          const val = c.val();
          if (val?.active) {
            const key = c.key!.replace(/^(wa_|web_|fb_|voice_)/, '');
            const age = now - (val.takenOverAt || 0);
            if (age > twoHours) {
              staleKeys.push(c.key!);
              console.log(`⏰ Auto-clearing stale agentMode: ${key} (${Math.round(age / 60000)}min old)`);
              return;
            }
            if (releasedSenders.current.has(key)) {
              console.log(`⏭️ Poll: skipping released sender ${key}`);
              return;
            }
            data[key] = true;
          }
        });
        for (const k of staleKeys) {
          remove(ref(db, `agentMode/${botId}/${k}`)).catch(() => {});
        }
      }
      setAgentModes(prev => {
        const prevKeys = Object.keys(prev).sort().join(',');
        const newKeys = Object.keys(data).sort().join(',');
        if (prevKeys === newKeys) return prev;
        console.log(`🔄 agentModes: [${prevKeys || 'none'}] → [${newKeys || 'none'}]`);
        return data;
      });
    } catch (e) { /* silent */ }
  };

  const handleTakeOver = async (botId: string, sender: string) => {
    const confirmed = window.confirm(
      `⚠️ Take Over Conversation\n\n` +
      `You are about to take over this ${agentChannelLabel(bot?.botType)} conversation.\n\n` +
      `• The bot will STOP replying immediately\n` +
      `• The customer will be notified a human agent joined\n` +
      `• Best done after the bot completes a response or at a handoff point — messages may clash if mid-flow\n\n` +
      `Continue?`
    );
    if (!confirmed) return;
    const endpoint = agentEndpoint(bot?.botType);
    const url = `${API_BASE}/api/${endpoint}/takeover/${botId}/${sender}`;
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify({ agentName: currentUser?.displayName || currentUser?.email || 'Agent' }),
      });
      const data = await res.json();
      if (data.success) {
        setAgentModes(prev => ({ ...prev, [sender]: true }));
        if (data.notifyError) alert('Taken over, but could not notify the customer: ' + data.notifyError);
      } else {
        alert('Failed to take over: ' + (data.error || 'Unknown error'));
      }
    } catch (e: any) { alert('Failed to take over: ' + e.message); }
  };

  const handleRelease = async (botId: string, sender: string) => {
    if (releasingRef.current || isReleasing) return;
    const confirmed = window.confirm(
      `🤖 Return to Bot\n\n` +
      `Hand this conversation back to the automated assistant?\n\n` +
      `• The customer will be notified the bot is resuming\n` +
      `• The bot will start responding to new messages\n\n` +
      `Continue?`
    );
    if (!confirmed) return;
    releasingRef.current = true;
    setIsReleasing(true);
    releasedSenders.current.add(sender);
    const endpoint = agentEndpoint(bot?.botType);
    const url = `${API_BASE}/api/${endpoint}/release/${botId}/${sender}`;
    setAgentModes(prev => { const n = { ...prev }; delete n[sender]; return n; });
    try {
      const res = await apiFetch(url, { method: 'POST' });
      const data = await res.json();
      if (!data.success) {
        setAgentModes(prev => ({ ...prev, [sender]: true }));
        releasedSenders.current.delete(sender);
        alert('Failed to release: ' + (data.error || 'Unknown error'));
      } else if (data.notifyError) {
        alert('Released, but could not notify the customer: ' + data.notifyError);
      }
    } catch (e: any) {
      setAgentModes(prev => ({ ...prev, [sender]: true }));
      releasedSenders.current.delete(sender);
      alert('Failed to release: ' + e.message);
    } finally {
      setTimeout(() => {
        releasingRef.current = false;
        setIsReleasing(false);
        releasedSenders.current.delete(sender);
        console.log(`🔓 Release lock cleared for ${sender}`);
      }, 10000);
    }
  };

  const handleAgentReply = async (botId: string, sender: string) => {
    if (!agentReply.trim() || agentSending) return;
    setAgentSending(true);
    const endpoint = agentEndpoint(bot?.botType);
    const url = `${API_BASE}/api/${endpoint}/reply/${botId}/${sender}`;
    const messageToSend = agentReply.trim();
    setAgentReply('');
    try {
      const res = await apiFetch(url, {
        method: 'POST',
        body: JSON.stringify({
          message: messageToSend,
          agentName: currentUser?.displayName || currentUser?.email || 'Agent',
        }),
      });
      const data = await res.json();
      if (!data.success) {
        setAgentReply(messageToSend);
        const msg = data.friendly || data.error || 'Unknown error';
        alert('Failed to send: ' + msg + (data.action && data.actionUrl ? `\n\n${data.action}: ${data.actionUrl}` : ''));
        return;
      }
      setTimeout(() => { refreshAnalytics(true); }, 1000);
    } catch (e: any) {
      setAgentReply(messageToSend);
      alert('Network error: ' + e.message);
    } finally {
      setAgentSending(false);
      agentInputRef.current?.focus();
    }
  };

  // Load agent handoff state on mount, then keep both it and analytics warm
  // while this tab is open — this used to be gated on activeTab==='conversations'
  // inside the monolithic Dashboard; now the route's own mount/unmount IS that
  // gate, so the loop just runs for as long as this component is alive.
  useEffect(() => {
    if (!bot) return;
    const botId = bot.id;
    let alive = true;
    loadAgentModes(botId);

    function schedule() {
      const delay = hasActiveAgentRef.current ? 3000 : 5000;
      return setTimeout(async () => {
        if (!alive) return;
        await Promise.all([refreshAnalytics(true), loadAgentModes(botId)]);
        if (alive) timer = schedule();
      }, delay);
    }
    let timer = schedule();

    return () => { alive = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot?.id]);

  useEffect(() => {
    hasActiveAgentRef.current = Object.keys(agentModes).length > 0;
  });

  if (!bot) return null;

  const threads = analytics?.recentConversations?.reduce((acc, conv) => {
    if (!acc[conv.userId]) acc[conv.userId] = [];
    acc[conv.userId].push(conv);
    return acc;
  }, {} as Record<string, typeof analytics.recentConversations>) || {};

  const threadUsers = Object.keys(threads).filter(uid =>
    threads[uid].some(c => !c.message?.startsWith('[AGENT] '))
  );
  const activeThread = selectedConv ? threads[selectedConv] : null;

  function FlowDiagram() {
    if (!analytics?.recentConversations?.length) return (
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '40px', textAlign: 'center', color: 'var(--text-faint)' }}>
        No conversation data to diagram yet. Select a contact on the left to view their thread.
      </div>
    );

    const triggerCounts: Record<string, number> = {};
    const triggerPaths: Record<string, Set<string>> = {};
    analytics.recentConversations.forEach((conv, i) => {
      const msg = conv.message.toLowerCase().trim();
      triggerCounts[msg] = (triggerCounts[msg] || 0) + 1;
      const next = analytics.recentConversations[i + 1];
      if (next && next.userId === conv.userId) {
        if (!triggerPaths[msg]) triggerPaths[msg] = new Set();
        triggerPaths[msg].add(next.message.toLowerCase().trim());
      }
    });

    const sorted = Object.entries(triggerCounts).sort((a, b) => b[1] - a[1]).slice(0, 12);
    const maxCount = sorted[0]?.[1] || 1;
    const colors = ['#5B4FE8', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#14b8a6', '#a855f7'];

    return (
      <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', padding: '24px', flex: 1 }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>Conversation Flow Map</div>
        <div style={{ color: 'var(--text-faint)', fontSize: '12px', marginBottom: '24px' }}>Most common triggers — bar width shows frequency. Arrows show what users type next.</div>

        {sorted.map(([trigger, count], i) => {
          const width = Math.max(8, (count / maxCount) * 100);
          const paths = triggerPaths[trigger] ? Array.from(triggerPaths[trigger]).slice(0, 3) : [];
          const color = colors[i % colors.length];
          return (
            <div key={trigger} style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <div style={{ width: '110px', fontSize: '12px', color: 'var(--text-primary)', fontWeight: '600', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{trigger}</div>
                <div style={{ flex: 1, height: '26px', background: 'var(--bg-page)', borderRadius: '6px', overflow: 'hidden', position: 'relative' }}>
                  <div style={{ width: `${width}%`, height: '100%', background: color, borderRadius: '6px', opacity: 0.85 }} />
                  <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: 'white', fontWeight: '600' }}>
                    {count}×
                  </span>
                </div>
              </div>
              {paths.length > 0 && (
                <div style={{ marginLeft: '122px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {paths.map(p => (
                    <span key={p} style={{ fontSize: '10px', color, background: color + '15', border: `1px solid ${color}30`, padding: '2px 8px', borderRadius: '10px' }}>
                      → {p}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--bg-element)', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {[
            { val: threadUsers.length, label: 'Unique users' },
            { val: analytics.recentConversations.length, label: 'Total messages' },
            { val: (analytics.recentConversations.length / (threadUsers.length || 1)).toFixed(1), label: 'Avg msgs/user' },
            { val: sorted[0]?.[0] || '—', label: 'Top trigger' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: '20px', fontWeight: '700' }}>{s.val}</div>
              <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 230px)', minHeight: '480px' }}
      onKeyDown={e => { if (agentModes[selectedConv || '']) e.stopPropagation(); }}
      onKeyUp={e => { if (agentModes[selectedConv || '']) e.stopPropagation(); }}
    >
      {/* Left — contact list */}
      <div style={{ width: '250px', flexShrink: 0, background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--bg-element)' }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', marginBottom: '8px' }}>
            Contacts ({threadUsers.length})
          </div>
          <input type="text" placeholder="Search name or number..." value={searchPhone}
            onChange={e => { setSearchPhone(e.target.value); setSelectedConv(null); }}
            style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' as const }} />
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {threadUsers
            .filter(uid => !searchPhone ||
              uid.includes(searchPhone) ||
              formatPhone(uid).includes(searchPhone) ||
              (contacts[uid]?.name || '').toLowerCase().includes(searchPhone.toLowerCase()) ||
              (contacts[uid]?.customer_name || '').toLowerCase().includes(searchPhone.toLowerCase())
            )
            .map(uid => {
              const contact = contacts[uid] || {};
              const displayName = contact.name || contact.customer_name || null;
              const lastMsg = threads[uid]?.filter(c => !c.message?.startsWith('[AGENT] ')).slice(-1)[0];
              const isSelected = selectedConv === uid;
              return (
                <div key={uid} onClick={() => setSelectedConv(uid === selectedConv ? null : uid)}
                  style={{
                    padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid var(--bg-element)',
                    background: isSelected ? '#5B4FE820' : 'transparent',
                    borderLeft: isSelected ? '3px solid #5B4FE8' : '3px solid transparent',
                  }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--text-primary)', fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {displayName || formatPhone(uid)}
                      </div>
                      {displayName && (
                        <div style={{ color: 'var(--text-faint)', fontSize: '10px', fontFamily: 'monospace' }}>{formatPhone(uid)}</div>
                      )}
                      {lastMsg && (
                        <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {lastMsg.message.slice(0, 28)}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ background: '#5B4FE820', color: '#818cf8', fontSize: '10px', padding: '2px 6px', borderRadius: '8px', fontWeight: '600' }}>
                        {threads[uid]?.filter(c => !c.message?.startsWith('[AGENT] ')).length}
                      </div>
                      {lastMsg && (
                        <div style={{ color: 'var(--text-faint)', fontSize: '9px', marginTop: '3px' }}>
                          {new Date(lastMsg.timestamp).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--bg-element)' }}>
          <button onClick={() => {
            const csv = (analytics?.recentConversations || []).map(c => {
              const contact = contacts[c.userId] || {};
              return `"${formatPhone(c.userId)}","${contact.name || contact.customer_name || ''}","${c.message.replace(/"/g, '')}","${c.response.replace(/"/g, '')}","${new Date(c.timestamp).toLocaleString()}"`;
            }).join('\n');
            const blob = new Blob(['Phone,Name,Message,Response,Time\n' + csv], { type: 'text/csv' });
            const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
            a.download = `conversations_${new Date().toISOString().split('T')[0]}.csv`; a.click();
          }} style={{ width: '100%', padding: '7px', background: 'var(--bg-element)', border: 'none', color: 'var(--text-muted)', borderRadius: '7px', fontSize: '12px', cursor: 'pointer' }}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Right — thread view or flow diagram */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {!activeThread ? (
          <FlowDiagram />
        ) : (
          <div style={{ background: 'var(--bg-card)', borderRadius: '12px', border: '1px solid var(--card-border)', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Thread header */}
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--bg-element)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: '700' }}>
                  {contacts[selectedConv!]?.name || contacts[selectedConv!]?.customer_name || formatPhone(selectedConv!)}
                </div>
                {(contacts[selectedConv!]?.name || contacts[selectedConv!]?.customer_name) && (
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', fontFamily: 'monospace' }}>{formatPhone(selectedConv!)}</div>
                )}
                {contacts[selectedConv!]?.email && (
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>📧 {contacts[selectedConv!].email}</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Agent mode indicator + Take Over / Release button */}
                {agentModes[selectedConv!] ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#10b98115', border: '1px solid #10b98130', borderRadius: '20px', padding: '4px 10px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '3px', background: '#10b981' }} />
                      <span style={{ fontSize: '11px', color: '#10b981', fontWeight: '600' }}>Agent Active</span>
                    </div>
                    <button
                      onClick={() => bot && !isReleasing && handleRelease(bot.id, selectedConv!)}
                      disabled={isReleasing}
                      style={{ padding: '6px 14px', background: isReleasing ? 'var(--bg-page)' : 'var(--bg-element)', color: isReleasing ? 'var(--text-faint)' : 'var(--text-secondary)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: isReleasing ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: isReleasing ? 0.5 : 1 }}
                    >
                      {isReleasing ? '⏳ Releasing...' : '🤖 Return to Bot'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => bot && handleTakeOver(bot.id, selectedConv!)}
                    style={{ padding: '6px 14px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
                  >
                    {bot?.botType === 'whatsapp' ? '💬 Take Over' : '🙋 Take Over'}
                  </button>
                )}
                <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>{activeThread.length} messages</div>
                <button onClick={() => setSelectedConv(null)} style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
              </div>
            </div>

            {/* Messages — column-reverse keeps newest at bottom and scroll position stable */}
            <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column-reverse', gap: '14px' }}>
              <div ref={messagesEndRef} style={{ height: 1 }} />
              {[...activeThread].map((conv, i) => {
                const isAgentMsg = conv.message?.startsWith('[AGENT] ');
                const agentText = isAgentMsg ? conv.message.replace('[AGENT] ', '') : null;

                if (isAgentMsg) {
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ maxWidth: '68%', background: 'var(--bg-page)', border: '1px solid #10b98140', borderLeft: '3px solid #10b981', borderRadius: '4px 14px 14px 14px', padding: '10px 14px' }}>
                        <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '600', marginBottom: '4px' }}>👤 Agent</div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>{agentText}</div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '4px' }}>
                          {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={i}>
                    {/* User message — right aligned, purple */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                      <div style={{ maxWidth: '68%', background: '#5B4FE8', borderRadius: '14px 14px 4px 14px', padding: '10px 14px' }}>
                        <div style={{ color: 'white', fontSize: '13px', lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>{conv.message}</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px', marginTop: '4px', textAlign: 'right' }}>
                          👤 {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                    {/* Bot response — left aligned, hidden for agent mode messages */}
                    {conv.response && conv.response !== '[waiting for agent]' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <div style={{ maxWidth: '68%', background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: '4px 14px 14px 14px', padding: '10px 14px' }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: '13px', lineHeight: '1.55', whiteSpace: 'pre-wrap' }}>{conv.response}</div>
                          <div style={{ color: 'var(--text-faint)', fontSize: '10px', marginTop: '4px' }}>
                            🤖 {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Agent reply input — only shown when in agent mode */}
            {agentModes[selectedConv!] && (
              <div
                style={{ padding: '12px 20px', borderTop: '1px solid var(--bg-element)', background: 'var(--bg-page)', display: 'flex', gap: '10px', alignItems: 'center' }}
                onClick={() => agentInputRef.current?.focus()}
              >
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: '24px', padding: '8px 16px', gap: '8px' }}>
                  <span style={{ fontSize: '13px' }}>👤</span>
                  <input
                    ref={agentInputRef}
                    type="text"
                    value={agentReply}
                    onChange={e => setAgentReply(e.target.value)}
                    onKeyDown={e => {
                      e.nativeEvent.stopImmediatePropagation();
                      e.stopPropagation();
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        bot && handleAgentReply(bot.id, selectedConv!);
                      }
                    }}
                    onKeyUp={e => { e.nativeEvent.stopImmediatePropagation(); e.stopPropagation(); }}
                    onBlur={() => {
                      if (agentModes[selectedConv || '']) {
                        setTimeout(() => agentInputRef.current?.focus(), 10);
                      }
                    }}
                    placeholder={bot?.botType === 'whatsapp' ? 'Type reply — sends to customer WhatsApp...' : 'Type your reply as agent...'}
                    style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '13px' }}
                    autoComplete="off"
                    autoFocus
                  />
                </div>
                <button
                  onClick={e => { e.stopPropagation(); bot && handleAgentReply(bot.id, selectedConv!); }}
                  disabled={agentSending || !agentReply.trim()}
                  style={{ padding: '8px 16px', background: agentSending || !agentReply.trim() ? 'var(--bg-element)' : '#10b981', color: agentSending || !agentReply.trim() ? 'var(--text-faint)' : 'white', border: 'none', borderRadius: '20px', cursor: agentSending || !agentReply.trim() ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap' }}
                >
                  {agentSending ? 'Sending...' : 'Send ➤'}
                </button>
              </div>
            )}

            {/* Contact details footer */}
            {contacts[selectedConv!] && Object.keys(contacts[selectedConv!]).filter(k => !['updatedAt', 'phone'].includes(k)).length > 0 && (
              <div style={{ padding: '12px 20px', borderTop: '1px solid var(--bg-element)', display: 'flex', gap: '20px', flexWrap: 'wrap', background: 'var(--bg-page)' }}>
                {Object.entries(contacts[selectedConv!])
                  .filter(([k]) => !['updatedAt', 'phone'].includes(k))
                  .map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize: '10px', color: 'var(--text-faint)', textTransform: 'capitalize', marginBottom: '2px' }}>{k.replace(/_/g, ' ')}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)', fontWeight: '500' }}>{String(v)}</div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
